// Shared primitives + formatters for the WxCC Operations Center.
// Kept intentionally lightweight — no business logic.

import React from 'react';
import { Alert, Anchor, Badge, Grid, Group, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';

export function Breadcrumbs({ items }) {
  return (
    <Group gap={4}>
      {items.map((it, i) => (
        <React.Fragment key={i}>
          {i > 0 && (
            <Text size="xs" c="dimmed">
              /
            </Text>
          )}
          {it.onClick ? (
            <Anchor size="xs" onClick={it.onClick} style={{ cursor: 'pointer' }}>
              {it.label}
            </Anchor>
          ) : (
            <Text size="xs" c="dimmed">
              {it.label}
            </Text>
          )}
        </React.Fragment>
      ))}
    </Group>
  );
}

export function StateBadge({ state }) {
  if (!state) return null;
  const color = state === 'open' ? 'green' : state === 'closed' ? 'gray' : 'yellow';
  return (
    <Badge size="xs" variant="light" color={color}>
      {state}
    </Badge>
  );
}

export function ErrorAlert({ message }) {
  return (
    <Alert color="red" icon={<IconAlertTriangle size={16} />} title="MCP call failed">
      <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>
        {message}
      </Text>
    </Alert>
  );
}

export function OutcomeBadge({ outcome }) {
  if (!outcome) return <Text size="xs" c="dimmed">—</Text>;
  const color =
    outcome === 'HANDLED' || outcome === 'COMPLETED' ? 'teal' :
    outcome === 'ESCALATED' ? 'blue' :
    outcome === 'ABANDONED' ? 'orange' :
    outcome === 'FAILED' ? 'red' : 'gray';
  return <Badge size="xs" color={color} variant="light">{outcome}</Badge>;
}

export function StatusBadge({ status }) {
  if (!status) return null;
  const color = status === 'ended' ? 'gray' : status === 'active' ? 'green' : 'blue';
  return <Badge size="xs" color={color} variant="light">{status}</Badge>;
}

export function HeaderStat({ label, value, mono, raw }) {
  return (
    <Grid.Col span={{ base: 6, sm: 3 }}>
      <Text size="xs" c="dimmed">{label}</Text>
      {raw ? value : (
        <Text size="sm" fw={500} style={mono ? { fontFamily: 'monospace' } : undefined}>
          {value ?? '—'}
        </Text>
      )}
    </Grid.Col>
  );
}

export function Metric({ label, value, hint }) {
  return (
    <Grid.Col span={{ base: 6, md: 3 }}>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="lg" fw={600}>
        {value}
      </Text>
      {hint && (
        <Text size="xs" c="dimmed" lineClamp={2}>
          {hint}
        </Text>
      )}
    </Grid.Col>
  );
}

export function short(id) {
  if (!id) return '';
  return id.length > 16 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}

export function fmtTs(v, timeOnly = false) {
  if (v == null) return '';
  const n = typeof v === 'number' ? v : Number(v);
  const d = !isNaN(n) && n > 1e12 ? new Date(n) : new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return timeOnly ? d.toLocaleTimeString() : d.toLocaleString();
}

export function fmtDuration(ms) {
  if (ms == null || !Number.isFinite(ms)) return '—';
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h) return `${h}h ${m}m ${s}s`;
  if (m) return `${m}m ${s}s`;
  return `${s}s`;
}

// Format a Date as "YYYY-MM-DDTHH:MM" for <input type="datetime-local">.
export function toDatetimeLocal(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
